import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@afosto/app-components-react/Autocomplete';
import { filters as filtersApi } from '@afosto/order/api/Filters';

const FiltersFilter = ({ label, onChange, operator, settings, value, ...props }) => {
  const [availableFilters, setAvailableFilters] = useState([]);
  const [availableFilterOptions, setAvailableFilterOptions] = useState([]);
  const [isFetchingFilters, setIsFetchingFilters] = useState(false);
  const [isFetchingFilterOptions, setIsFetchingFilterOptions] = useState(false);
  const [filter, setFilter] = useState([]);
  const [filterOptions, setFilterOptions] = useState([]);
  const valueRef = useRef(value);
  const selectedFilterKey = filter?.value;

  useEffect(() => {
    valueRef.current = value;
  });

  useEffect(() => {
    let isMounted = true;

    const fetchFilters = async () => {
      setIsFetchingFilters(true);

      try {
        const response = await filtersApi.getFilterKeys();
        const { data = [] } = response;
        const formattedFilters = data.reduce(
          (acc, filterKey) => [
            ...acc,
            {
              label: filterKey,
              value: filterKey,
            },
          ],
          [],
        );

        if (isMounted) {
          setAvailableFilters(formattedFilters);
        }
      } catch (err) {
        if (isMounted) {
          setAvailableFilters([]);
        }
      } finally {
        if (isMounted) {
          setIsFetchingFilters(false);
        }
      }
    };

    fetchFilters();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchFilterOptions = async () => {
      setIsFetchingFilterOptions(true);

      try {
        const response = await filtersApi.getFilterValues(selectedFilterKey);
        const { data = [] } = response;
        const formattedOptions = data.reduce(
          (acc, filterOption) => [...acc, { label: filterOption, value: filterOption }],
          [],
        );

        if (isMounted) {
          setAvailableFilterOptions(formattedOptions);
        }
      } catch (err) {
        if (isMounted) {
          setAvailableFilterOptions([]);
        }
      } finally {
        if (isMounted) {
          setIsFetchingFilterOptions(false);
        }
      }
    };

    if (selectedFilterKey) {
      fetchFilterOptions();
    }

    return () => {
      isMounted = false;
    };
  }, [selectedFilterKey]);

  useEffect(() => {
    let isMounted = true;
    const initialValue = valueRef.current;

    if (initialValue && initialValue?.length > 0) {
      const [firstValue] = initialValue;
      const [filterKey] = firstValue.toString().split(':');

      if (isMounted && filterKey) {
        setFilter({
          label: filterKey,
          value: filterKey,
        });
      }

      const filterOptionValues = initialValue.reduce((acc, filterOptionValue) => {
        const [, optionValue] = filterOptionValue.toString().split(':');

        if (optionValue) {
          return [
            ...acc,
            {
              label: optionValue,
              value: optionValue,
            },
          ];
        }

        return acc;
      }, []);

      if (isMounted) {
        setFilterOptions(filterOptionValues);
      }
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChangeFilter = (ev, selectedFilter) => {
    setFilter(selectedFilter);
    setAvailableFilterOptions([]);
    setFilterOptions([]);

    if (onChange && typeof onChange === 'function') {
      onChange(ev, []);
    }
  };

  const handleChangeFilterOptions = (ev, selectedOptions) => {
    setFilterOptions(selectedOptions);

    if (onChange && typeof onChange === 'function') {
      const values = selectedOptions.reduce(
        (acc, selectedOption) => [...acc, `${selectedFilterKey}:${selectedOption.value}`],
        [],
      );

      onChange(ev, values);
    }
  };

  return (
    <>
      <Autocomplete
        {...props}
        name="filterKey"
        label="Filter key"
        value={filter}
        onChange={handleChangeFilter}
        options={availableFilters}
        loading={isFetchingFilters}
        autocomplete="off"
        enableCreate={false}
      />
      <Autocomplete
        {...props}
        name="filterOptions"
        label={label}
        value={filterOptions}
        onChange={handleChangeFilterOptions}
        options={availableFilterOptions}
        loading={isFetchingFilterOptions}
        enableCreate={false}
        autocomplete="off"
        disabled={!filter || !operator}
        multiple={!operator || operator === 'in'}
      />
    </>
  );
};

FiltersFilter.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  operator: PropTypes.string,
  settings: PropTypes.object,
  value: PropTypes.array,
};

FiltersFilter.defaultProps = {
  label: undefined,
  onChange: undefined,
  operator: undefined,
  settings: {},
  value: [],
};

export default FiltersFilter;
